import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'link' | 'destructive' | 'outline'
  size?: 'sm' | 'md' | 'lg' | 'icon'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        style={{
          transitionDuration: 'var(--duration-fast, 120ms)',
          transitionTimingFunction: 'var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1))',
        }}
        className={cn(
          'inline-flex items-center justify-center rounded-md font-medium',
          'transition-[transform,box-shadow,background-color,border-color,color]',
          'will-change-transform',
          'hover:-translate-y-px',
          'active:translate-y-0 active:scale-[0.97] active:transition-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50 disabled:hover:translate-y-0',
          {
            'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md focus-visible:ring-ring': variant === 'primary',
            'bg-card text-foreground border border-border hover:bg-muted hover:text-foreground hover:border-border hover:shadow-sm focus-visible:ring-ring': variant === 'secondary',
            'text-foreground border border-border hover:bg-muted hover:text-foreground hover:shadow-sm focus-visible:ring-ring': variant === 'outline',
            'text-muted-foreground hover:bg-muted hover:text-foreground hover:translate-y-0 focus-visible:ring-ring': variant === 'ghost',
            'text-foreground underline-offset-4 hover:underline hover:translate-y-0': variant === 'link',
            'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-md focus-visible:ring-destructive': variant === 'destructive',
          },
          {
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-sm': size === 'md',
            'px-6 py-3 text-base': size === 'lg',
            'h-9 w-9 p-0': size === 'icon',
          },
          className
        )}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
